import { bcs } from '@mysten/bcs';
import { JsonRpcHTTPTransport } from '@mysten/sui/jsonRpc';
import { Transaction } from '@mysten/sui/transactions';
import { dgcToMist, mistToDgc } from './amount.js';
import { DGC_TYPE, MIST_PER_RWAX } from './constants.js';
/** 链上锚定：DGC/DATA 1:1 → 每 1 DGC lot 的 DATA 报价（mist）。 */
export const DATA_DGC_REFERENCE_PRICE_TICKS = 1000000000n;
/** @deprecated use DATA_DGC_REFERENCE_PRICE_TICKS */
export const DATA_CPC_REFERENCE_PRICE_TICKS = DATA_DGC_REFERENCE_PRICE_TICKS;
/** 参考限价（可读）：每 1 DGC 的 DATA 价格，默认 1。 */
export const DATA_DGC_REFERENCE_DATA_PER_DGC = mistToDgc(DATA_DGC_REFERENCE_PRICE_TICKS);
/**
 * @deprecated 旧 DATA/DGC 语义（DGC per DATA）。DGC/DATA 请用 `DATA_DGC_REFERENCE_DATA_PER_DGC`。
 * 1:1 时数值仍为 `"1"`。
 */
export const DATA_DGC_REFERENCE_DGC_PER_DATA = DATA_DGC_REFERENCE_DATA_PER_DGC;
/** @deprecated use DATA_DGC_REFERENCE_DATA_PER_DGC */
export const DATA_CPC_REFERENCE_DGC_PER_DATA = DATA_DGC_REFERENCE_DATA_PER_DGC;
/** Resolve base type for a market config (DGC/DATA default). */
export function clobBaseType(config) {
    return config.baseType?.trim() || DGC_TYPE;
}
/** 交易所限价 → 链上 `price_ticks`（= 每 1 DGC 的 DATA mist）。 */
export function dataPerDgcToPriceTicks(dataPerDgc) {
    const s = dataPerDgc.trim();
    if (!s)
        throw new Error('请输入限价');
    let mist;
    try {
        mist = dgcToMist(s);
    }
    catch {
        throw new Error(`限价格式无效：${dataPerDgc}`);
    }
    if (mist <= 0n)
        throw new Error('限价须大于 0');
    return mist;
}
/** @deprecated use `dataPerDgcToPriceTicks`（DGC/DATA 主报价为 DATA per DGC）。 */
export function dgcPerDataToPriceTicks(dgcPerData) {
    return dataPerDgcToPriceTicks(dgcPerData);
}
/** 未填限价时使用参考价；否则解析 DATA/DGC（每 1 DGC 的 DATA）。 */
export function parseLimitPriceDataPerDgc(input, fallbackDataPerDgc = DATA_DGC_REFERENCE_DATA_PER_DGC) {
    const raw = input?.trim();
    if (!raw)
        return dataPerDgcToPriceTicks(fallbackDataPerDgc);
    return dataPerDgcToPriceTicks(raw);
}
/** @deprecated use `parseLimitPriceDataPerDgc` */
export function parseLimitPriceDgcPerData(input, fallbackDgcPerData = DATA_DGC_REFERENCE_DATA_PER_DGC) {
    return parseLimitPriceDataPerDgc(input, fallbackDgcPerData);
}
export function priceTicksToView(priceTicks) {
    const ticks = BigInt(priceTicks);
    if (ticks <= 0n) {
        return { priceTicks: '0', dgcPerData: '0', dataPerDgc: '0' };
    }
    const dataPerDgc = mistToDgc(ticks);
    const dgcPerDataRaw = Number(MIST_PER_RWAX) / Number(ticks);
    const dgcPerData = Number.isFinite(dgcPerDataRaw) && dgcPerDataRaw > 0
        ? trimFloat(dgcPerDataRaw)
        : '—';
    return {
        priceTicks: ticks.toString(),
        dataPerDgc,
        dgcPerData,
    };
}
function trimFloat(n) {
    if (Math.abs(n - Math.round(n)) < 1e-9)
        return String(Math.round(n));
    return n.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
}
function normalizePackageId(packageId) {
    return packageId.trim();
}
function tradeEventType(packageId) {
    return `${normalizePackageId(packageId)}::order_book::Trade`;
}
function parseTradeEvents(events) {
    const rows = [];
    for (const ev of events) {
        const row = parseTradeEvent(ev);
        if (row)
            rows.push(row);
    }
    return rows;
}
/** 直接 JSON-RPC（CPC 分叉 / 扩展 background 上比 SDK 更可靠）。 */
export async function queryTradeEventsRawViaJsonRpc(rpcUrl, packageId, limit = 20) {
    const url = rpcUrl.trim().replace(/\/+$/, '');
    if (!url)
        throw new Error('RPC URL 为空');
    const transport = new JsonRpcHTTPTransport({ url });
    const result = (await transport.request({
        method: 'suix_queryEvents',
        params: [{ MoveEventType: tradeEventType(packageId) }, null, limit, true],
    }));
    return result?.data ?? [];
}
export async function fetchTradeEventsViaJsonRpc(rpcUrl, packageId, limit = 20) {
    return parseTradeEvents(await queryTradeEventsRawViaJsonRpc(rpcUrl, packageId, limit));
}
export async function fetchRecentDataCpcTradesWithMeta(rpcUrl, packageId, limit = 20, client) {
    if (!client) {
        const raw = await queryTradeEventsRawViaJsonRpc(rpcUrl, packageId, limit);
        const meta = {
            rpcUrl: rpcUrl.trim().replace(/\/+$/, ''),
            packageId: normalizePackageId(packageId),
            eventType: tradeEventType(packageId),
            rawEventCount: raw.length,
            parsedCount: 0,
            source: 'jsonrpc',
        };
        const rows = parseTradeEvents(raw);
        meta.parsedCount = rows.length;
        return {
            rows,
            meta,
            debug: { rawEventsRpc: raw, rawEventsSdk: [], rpcError: null, sdkError: null },
        };
    }
    const dual = await queryDataCpcTradesDual(rpcUrl, packageId, client, limit);
    return {
        rows: dual.rows,
        meta: dual.meta,
        debug: {
            rawEventsRpc: dual.rawEventsRpc,
            rawEventsSdk: dual.rawEventsSdk,
            rpcError: dual.rpcError,
            sdkError: dual.sdkError,
        },
    };
}
async function fetchTradeEventsViaClient(client, packageId, limit) {
    const page = await client.queryEvents({
        query: { MoveEventType: tradeEventType(packageId) },
        limit,
        order: 'descending',
    });
    return parseTradeEvents(page.data ?? []);
}
function parseTradeEvent(ev) {
    if (!ev.parsedJson || typeof ev.parsedJson !== 'object')
        return null;
    const body = ev.parsedJson;
    const priceTicks = String(body.price_ticks ?? body.priceTicks ?? '');
    const qtyLots = String(body.qty_lots ?? body.qtyLots ?? '');
    const maker = String(body.maker ?? '');
    const taker = String(body.taker ?? '');
    if (!priceTicks || priceTicks === '0' || !qtyLots)
        return null;
    const ts = ev.timestampMs != null ? Number(ev.timestampMs) : null;
    const digest = ev.id?.txDigest ?? '';
    return {
        ...priceTicksToView(priceTicks),
        qtyLots,
        timestampMs: ts != null && Number.isFinite(ts) ? ts : null,
        digest,
        maker,
        taker,
    };
}
/** 双通道查询并返回完整源数据（钱包调试 / 回退）。 */
export async function queryDataCpcTradesDual(rpcUrl, packageId, client, limit = 20) {
    const eventType = tradeEventType(packageId);
    const meta = {
        rpcUrl: rpcUrl.trim().replace(/\/+$/, ''),
        packageId: normalizePackageId(packageId),
        eventType,
        rawEventCount: 0,
        parsedCount: 0,
        source: 'jsonrpc',
    };
    let rawEventsRpc = [];
    let rawEventsSdk = [];
    let rpcError = null;
    let sdkError = null;
    try {
        rawEventsRpc = await queryTradeEventsRawViaJsonRpc(meta.rpcUrl, meta.packageId, limit);
    }
    catch (e) {
        rpcError = e instanceof Error ? e.message : String(e);
    }
    try {
        const page = await client.queryEvents({
            query: { MoveEventType: eventType },
            limit,
            order: 'descending',
        });
        rawEventsSdk = page.data ?? [];
    }
    catch (e) {
        sdkError = e instanceof Error ? e.message : String(e);
    }
    const raw = rawEventsRpc.length >= rawEventsSdk.length ? rawEventsRpc : rawEventsSdk;
    meta.rawEventCount = raw.length;
    meta.source = rawEventsRpc.length >= rawEventsSdk.length ? 'jsonrpc' : 'sdk';
    const rows = parseTradeEvents(raw);
    meta.parsedCount = rows.length;
    return { rows, meta, rawEventsRpc, rawEventsSdk, rpcError, sdkError };
}
export async function fetchLastDataCpcTrade(client, packageId, rpcUrl) {
    let rows = [];
    try {
        rows = await fetchTradeEventsViaClient(client, packageId, 1);
    }
    catch {
        /* SDK path failed */
    }
    if (rows.length === 0 && rpcUrl) {
        rows = await fetchTradeEventsViaJsonRpc(rpcUrl, packageId, 1);
    }
    const row = rows[0] ?? null;
    if (!row)
        return null;
    const { maker: _m, taker: _t, ...rest } = row;
    return rest;
}
/** 最近市场成交（链上 `Trade` 事件，降序）。有钱包 RPC 时优先 JSON-RPC。 */
export async function fetchRecentDataCpcTrades(client, packageId, limit = 20, rpcUrl) {
    if (rpcUrl?.trim()) {
        const { rows } = await fetchRecentDataCpcTradesWithMeta(rpcUrl, packageId, limit, client);
        return rows;
    }
    return fetchTradeEventsViaClient(client, packageId, limit);
}
function normAddress(addr) {
    return addr.trim().toLowerCase();
}
export function filterMyDataCpcTrades(trades, owner) {
    const me = normAddress(owner);
    return (trades ?? []).filter((t) => normAddress(t.maker) === me || normAddress(t.taker) === me);
}
/** 链上实时买一 / 卖一（devInspect 调 `order_book::best_bid_ask_ticks`）。 */
export async function fetchBestBidAsk(client, config, sender = '0x0') {
    if (!client.devInspectTransactionBlock) {
        return { ...(await fetchRecentOrderHints(client, config.packageId)), live: false };
    }
    const tx = new Transaction();
    tx.moveCall({
        target: `${config.packageId}::order_book::best_bid_ask_ticks`,
        typeArguments: [clobBaseType(config), config.dataTypeTag],
        arguments: [tx.object(config.orderBookId)],
    });
    let inspect;
    try {
        inspect = await client.devInspectTransactionBlock({ sender, transactionBlock: tx });
    }
    catch {
        return { ...(await fetchRecentOrderHints(client, config.packageId)), live: false };
    }
    const status = inspect.effects?.status?.status;
    if (status && status !== 'success') {
        return { ...(await fetchRecentOrderHints(client, config.packageId)), live: false };
    }
    const returnValues = inspect.results?.[0]?.returnValues;
    if (!returnValues || returnValues.length < 2) {
        return { ...(await fetchRecentOrderHints(client, config.packageId)), live: false };
    }
    const bestBid = parseDevInspectU64(returnValues[0]);
    const bestAsk = parseDevInspectU64(returnValues[1]);
    return {
        bestBidTicks: bestBid > 0n ? bestBid.toString() : null,
        bestAskTicks: bestAsk > 0n ? bestAsk.toString() : null,
        bestBid: bestBid > 0n ? priceTicksToView(bestBid) : null,
        bestAsk: bestAsk > 0n ? priceTicksToView(bestAsk) : null,
        live: true,
    };
}
function parseDevInspectU64(tuple) {
    if (!tuple?.[0]?.length)
        return 0n;
    try {
        return BigInt(bcs.u64().parse(new Uint8Array(tuple[0])));
    }
    catch {
        return 0n;
    }
}
function orderPlacedEventType(packageId) {
    return `${normalizePackageId(packageId)}::order_book::OrderPlaced`;
}
function parseDevInspectU64Vector(tuple) {
    if (!tuple?.[0]?.length)
        return [];
    try {
        const raw = bcs.vector(bcs.u64()).parse(new Uint8Array(tuple[0]));
        return raw.map((v) => (typeof v === 'bigint' ? v : BigInt(v)));
    }
    catch {
        return [];
    }
}
function levelsFromPriceQtyVectors(prices, qtys) {
    const n = Math.min(prices.length, qtys.length);
    const rows = [];
    for (let i = 0; i < n; i++) {
        const ticks = prices[i];
        const qty = qtys[i];
        if (ticks <= 0n || qty <= 0n)
            continue;
        rows.push({
            ...priceTicksToView(ticks),
            qtyLots: qty.toString(),
        });
    }
    return rows;
}
/** 链上 OrderBook 实时五档（devInspect `book_depth_snapshot`）。 */
export async function fetchOrderBookDepthFromChain(client, config, depth = 5, sender = '0x0') {
    if (!client.devInspectTransactionBlock)
        return null;
    const tx = new Transaction();
    tx.moveCall({
        target: `${config.packageId}::order_book::book_depth_snapshot`,
        typeArguments: [clobBaseType(config), config.dataTypeTag],
        arguments: [tx.object(config.orderBookId), tx.pure.u64(depth)],
    });
    let inspect;
    try {
        inspect = await client.devInspectTransactionBlock({ sender, transactionBlock: tx });
    }
    catch {
        return null;
    }
    const status = inspect.effects?.status?.status;
    if (status && status !== 'success')
        return null;
    const rv = inspect.results?.[0]?.returnValues;
    if (!rv || rv.length < 4)
        return null;
    const bidPrices = parseDevInspectU64Vector(rv[0]);
    const bidQtys = parseDevInspectU64Vector(rv[1]);
    const askPrices = parseDevInspectU64Vector(rv[2]);
    const askQtys = parseDevInspectU64Vector(rv[3]);
    return {
        bids: levelsFromPriceQtyVectors(bidPrices, bidQtys),
        asks: levelsFromPriceQtyVectors(askPrices, askQtys),
    };
}
/** 从 `OrderPlaced` 事件聚合档位（包未升级时的回退）。 */
export async function fetchOrderBookDepthFromEvents(client, packageId, depth = 5, eventLimit = 256) {
    const page = await client.queryEvents({
        query: { MoveEventType: orderPlacedEventType(packageId) },
        limit: eventLimit,
        order: 'descending',
    });
    const bidQty = new Map();
    const askQty = new Map();
    for (const ev of page.data ?? []) {
        if (!ev.parsedJson || typeof ev.parsedJson !== 'object')
            continue;
        const body = ev.parsedJson;
        const ticksRaw = body.price_ticks ?? body.priceTicks;
        const qtyRaw = body.qty_lots ?? body.qtyLots;
        if (ticksRaw == null || qtyRaw == null)
            continue;
        let ticks;
        let qty;
        try {
            ticks = BigInt(String(ticksRaw));
            qty = BigInt(String(qtyRaw));
        }
        catch {
            continue;
        }
        if (ticks <= 0n || qty <= 0n)
            continue;
        const key = ticks.toString();
        const isAsk = Boolean(body.is_ask ?? body.isAsk);
        const book = isAsk ? askQty : bidQty;
        book.set(key, (book.get(key) ?? 0n) + qty);
    }
    const toLevels = (entries, desc) => {
        const sorted = [...entries.entries()].sort((a, b) => {
            const ta = BigInt(a[0]);
            const tb = BigInt(b[0]);
            return desc ? (ta > tb ? -1 : ta < tb ? 1 : 0) : ta < tb ? -1 : ta > tb ? 1 : 0;
        });
        return sorted.slice(0, depth).map(([ticks, qtyLots]) => ({
            ...priceTicksToView(ticks),
            qtyLots: qtyLots.toString(),
        }));
    };
    return {
        bids: toLevels(bidQty, true),
        asks: toLevels(askQty, false),
    };
}
/**
 * 盘口五档：优先链上挂单快照，否则回退事件聚合。
 * 买盘价高→低，卖盘价低→高。
 */
export async function fetchOrderBookDepth(client, config, depth = 5, sender = '0x0') {
    const fromChain = await fetchOrderBookDepthFromChain(client, config, depth, sender);
    if (fromChain && (fromChain.bids.length > 0 || fromChain.asks.length > 0)) {
        return { ...fromChain, live: true };
    }
    const fromEvents = await fetchOrderBookDepthFromEvents(client, config.packageId, depth);
    return { ...fromEvents, live: false };
}
/** 从近期 `OrderPlaced` 事件推断挂单参考价（旧包无 view 时的回退）。 */
export async function fetchRecentOrderHints(client, packageId, limit = 64) {
    const page = await client.queryEvents({
        query: { MoveEventType: orderPlacedEventType(packageId) },
        limit,
        order: 'descending',
    });
    let bestBid = null;
    let bestAsk = null;
    for (const ev of page.data ?? []) {
        if (!ev.parsedJson || typeof ev.parsedJson !== 'object')
            continue;
        const body = ev.parsedJson;
        const ticksRaw = body.price_ticks ?? body.priceTicks;
        if (ticksRaw == null)
            continue;
        let ticks;
        try {
            ticks = BigInt(String(ticksRaw));
        }
        catch {
            continue;
        }
        if (ticks <= 0n)
            continue;
        const isAsk = Boolean(body.is_ask ?? body.isAsk);
        if (isAsk) {
            if (bestAsk === null || ticks < bestAsk)
                bestAsk = ticks;
        }
        else if (bestBid === null || ticks > bestBid) {
            bestBid = ticks;
        }
    }
    return {
        bestBidTicks: bestBid?.toString() ?? null,
        bestAskTicks: bestAsk?.toString() ?? null,
        bestBid: bestBid != null ? priceTicksToView(bestBid) : null,
        bestAsk: bestAsk != null ? priceTicksToView(bestAsk) : null,
    };
}
export async function verifyClobObjectsReachable(client, config) {
    const ids = [config.registryId, config.orderBookId];
    for (const id of ids) {
        try {
            const resp = await client.getObject({ id, options: { showType: true } });
            if (!resp.data?.objectId)
                return false;
        }
        catch {
            return false;
        }
    }
    return true;
}
/** 与链上 escrow::quote_amount 一致：price_ticks × qty_lots × lot / 1e9 */
export function quoteDataMistForDgcBid(priceTicks, qtyLots, lotSizeMist = 1000000000n) {
    return (priceTicks * qtyLots * lotSizeMist) / 1000000000n;
}
/** @deprecated name; returns DATA mist for DGC/DATA bids (same formula). */
export function quoteDgcMistForDataBid(priceTicks, qtyLots, lotSizeMist = 1000000000n) {
    return quoteDataMistForDgcBid(priceTicks, qtyLots, lotSizeMist);
}
